import { connect, useSelector } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import { AppDispatch, RootState } from 'store/configureStore';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { API } from 'aws-amplify';
import { newNotification, Notification } from '../../actions/uiActions';
import SuggestedDropPointConfirmationModal from '../Restaurant/components/SuggestedDropPointConfirmationModal';
import { logToDatadog } from '../../services/datadog';
import { AddressFind, Cell, DropLocation } from '@m4a3/api-client';
import { Switch, Label, Field } from '@headlessui/react';
import { Circle, GeoJSON, MapContainer, Marker, Pane, Popup, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { geofenceColor, indexPriority } from '../../utils/ui';
import markerIconPng from "leaflet/dist/images/marker-icon.png"
import L, { Map } from 'leaflet';
import { OBSTACLE_COLORS } from '../RoutePlanner/ObstaclePlanner';
import { updateAddressEnabledStatus } from '../../actions/routePlannerActions';
import { convertMarkerSizeMetresToPixels } from '../../utils/mapUtils';
import { getGeofences } from '../../actions/geofenceActions';
import { getObstacles, getOsmObstacleDataForCoordinates } from '../../actions/obstacleActions';
import { selectObstacles } from '../../reducers/obstaclesReducer';
import { blanchCellId, dropPointRadiusInMetres } from '../../constants';
import { FeatureCollection } from 'geojson';
import { Obstacle } from '../../models/obstacle.interface';
import { obstacleData } from '../Obstacles/MBObstacles';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point, polygon } from '@turf/helpers';
import { AddressFindRemark } from '@m4a3/api-client/dist/models/address-find-remark.interface';
import { getCells } from '../../actions/orderActions';
import SelectDropdown from '../../components/SelectDropdown/SelectDropdown';
import { addressRemarkDropdownOptions } from '../../utils/addressFindUtils';
import { orderfulApi } from '../../services/api-instances';

interface DropPointSuggestorProps {
  notify: (notification: Notification) => void;
  updateAddressEnabledStatus: (addressId: string, isEnabled: boolean) => Promise<void>,
  getCells: () => void;
  cells: Cell[];
  selectedCellId: number;
  geofences: any;
  getGeofences: () => void;
  getObstacles: (cellId: number) => void;
}
function DropPointSuggestor ({
  notify,
  updateAddressEnabledStatus,
  getCells,
  cells,
  selectedCellId,
  geofences,
  getGeofences,
  getObstacles
}: Readonly<DropPointSuggestorProps>) {
  const obstacles: Obstacle[] = useSelector(selectObstacles);
  const mapRef =  useRef<Map>();
  const [eircode, setEircode] = useState<string>('');
  const [isAddressEnabled, setIsAddressEnabled] = useState<boolean>(false);
  const [addressInfo, setAddressInfo] = useState<AddressFind>(null);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoadingAddressInfo, setIsLoadingAddressInfo] = useState<boolean>(false);
  const [isEnablingAddress, setIsEnablingAddress] = useState<boolean>(false);
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(false);
  const [markers, setMarkers] = useState([]);
  const [osmObstacleData, setOsmObstacleData] = useState<FeatureCollection>();
  const [addressRemarkText, setAddressRemarkText] = useState<string>('');
  const [addressRemarks, setAddressRemarks] = useState<AddressFindRemark[]>([]);

  useEffect(() => {
    if (addressInfo?.address_latitude && addressInfo?.address_longitude) {
      getOsmObstacleDataForCoordinates(addressInfo?.address_latitude, addressInfo?.address_longitude)
        .then(data => {
          setOsmObstacleData(data)
        })
    }
  }, [addressInfo]);

  useEffect(() => {
    setIsFormValid(checkIsFormValid())
  }, [eircode]);

  useEffect(() => {
    getCells();
    getGeofences();
    getObstacles(blanchCellId);
  }, []);

  const InitializeMap = () => {
    const map = useMap();
    mapRef.current = map;

    useMapEvents({
      zoomend: () => {
        updateMarkerSizes();
      }
    });

    return null;
  };

  const getRemarksForAddress = async (addressFindId: string) => {
    orderfulApi.getRemarksForAddressfind(addressFindId)
      .then((remarks) => {
        setAddressRemarks(remarks as unknown as AddressFindRemark[]);
      });
  }

  const saveRemarkForAddress = async (addressFindId: string, message: string) => {
    orderfulApi.createAddressFindRemark(addressFindId, {
      message,
      isVisibleToCustomer: true,
      addressFindId
    })
  }

  const updateMarkerSizes = () => {
    const newMarkers = markers.map(marker => {
      const latitude = marker.getLatLng().lat;
      const sizeInPixels = convertMarkerSizeMetresToPixels(4.5, mapRef.current, latitude);
      const newIcon = L.icon({
        iconUrl: marker.options.icon.options.iconUrl,
        iconSize: [sizeInPixels, sizeInPixels],
      });
      marker.setIcon(newIcon);
      return marker;
    });
    setMarkers(newMarkers);
  };

  const checkIsFormValid = (): boolean => {
    const regex = new RegExp(/(?:^[AC-FHKNPRTV-Y][0-9]{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Y]{4}$/);
    return regex.test(eircode);
  }

  const fetchAddressInfo = async (eircode: string) => {
    if (!eircode) return;
    return API.get('api', `/addressfind/locationId/${eircode}`, {})
  };

  const onChangeEircode = (newEircode: string) => {
    const normalisedEircode = newEircode.replace(/\s/g, '').toUpperCase();
    setEircode(normalisedEircode)
  }

  const enableAddress = async (val: boolean) => {
    try {
      setIsEnablingAddress(true);
      updateAddressEnabledStatus(addressInfo.id, val);
      setIsAddressEnabled(val);
    } catch (error) {
      logToDatadog('error_enabling_address', {error, eircode, val}, 'error');
      notify({
        variant: 'error',
        message: error.errorMessage
      });
    }
    setIsEnablingAddress(false);
  };

  const checkIsWithinGeofence = (): boolean => {
    if (!cells) {
      return;
    }

    const selectedCell = cells?.find(cell => cell.id === blanchCellId);
    const addressPoint = point([addressInfo.address_longitude, addressInfo.address_latitude]);
    const cellGeofencePolygon = polygon(selectedCell?.geofence?.geoJson?.features[0]?.geometry?.coordinates);
    return booleanPointInPolygon(addressPoint, cellGeofencePolygon)
  }

  const loadAddressInfo = async () => {
    try {
      setIsLoadingAddressInfo(true);
      setAddressInfo(null);
      const address = await fetchAddressInfo(eircode);
      setAddressInfo(address);
      setIsAddressEnabled(address.enabled);
      await getRemarksForAddress(address.id)
    } catch (error) {
      logToDatadog('error_fetching_address', {error, eircode}, 'error');
      notify({
        variant: 'error',
        message: error.errorMessage
      });
    }
    setIsLoadingAddressInfo(false);
  }

  const getEircodeCoords = (): [number, number] => {
    return [addressInfo?.address_latitude, addressInfo?.address_longitude];
  }

  return (<div className='bg-white h-full'>
    <header className="p-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-semibold leading-6 text-gray-900">Drop Point Suggestions</h2>
          <p className="mt-2 text-sm text-gray-700">
            For Blanchardstown cell only.
          </p>
        </div>
      </div>
    </header>
    <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-6 p-6 h-full">
      <div className="sm:col-span-3 h-full">
        <div>
          <label htmlFor="eircode" className="block text-sm font-medium leading-6 text-gray-900">
            Eircode
          </label>
          <div className="mt-2">
            <input
              type="text"
              name="eircode"
              id="eircode"
              value={eircode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onChangeEircode(e.target.value)
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>{
                if (e.key == 'Enter' ){
                  loadAddressInfo();
                }
              }}
              autoComplete="postal-code"
              className="pl-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>
        {eircode && !isFormValid ? <div className="p-2 text-sm text-red-700">
          Invalid eircode
        </div> :<></>}
        <button
          type="button"
          disabled={!isFormValid || isLoadingAddressInfo}
          onClick={loadAddressInfo}
          className={`my-3 inline-flex items-center rounded-md ${isFormValid ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-200 cursor-not-allowed hover:bg-blue-200 text-slate-500'} px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
        >
          Load address info {isLoadingAddressInfo ? <div className='ml-2'> <LoadingSpinner size={20} /></div> : <></>}
        </button>
        {addressInfo ? <MapContainer
          style={{
            height: '100%',
            width: '100%',
            minHeight: 312
          }}
          ref={mapRef}
          whenReady={function() {
            // zoom prop is not changing the initial zoom past 17, so this is the workaround.
            this.setZoom(21);
          }}
          center={getEircodeCoords()}
          zoom={17}>
          <InitializeMap />
          <TileLayer
            attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <TileLayer
            attribution="Manna"
            url="https://tiles.manna.aero/{z}/{x}/{y}.png"
            maxZoom={24}
          />

          {obstacles?.map(ob => {
            const color = OBSTACLE_COLORS[ob.groundRisk] || 'red';
            return (
              <GeoJSON
                data={ob.geoJson}
                style={{
                  color,
                  weight: 2,
                  fillOpacity: 0.5
                }}
                key={Math.random() + 'xxxgeeeituerijfhy7er'}>
                <Popup>
                  <div>
                    <strong>Type:</strong> {obstacleData[ob.groundRisk].key}<br/>
                    {ob.name && <>
                      <strong>Name:</strong> {ob.name}<br/>
                    </>}
                  </div>
                </Popup>
            </GeoJSON>);
          })}
          {osmObstacleData?.features?.map(feature => {
            if (feature.geometry.type?.toLowerCase() === 'point') {
              return <Marker
                key={`point-marker-${(feature.geometry as any).coordinates}-${feature.properties.name || feature.properties.type}`}
                position={(feature as any).geometry.coordinates?.reverse()}
                icon={new L.Icon({
                  iconUrl: markerIconPng,
                  iconSize: [25, 41],
                })}>
                <Tooltip
                  permanent
                  direction="bottom"
                  opacity={0.6}
                  offset={[0, 10]}>
                  {feature.properties.name || feature.properties.type}
                </Tooltip>
              </Marker>
              } else {
              return (<GeoJSON
                data={feature}
                style={{
                  weight: 2,
                  fillOpacity: 0.1,
                  color: 'red'
                }}
                key={Math.random() + 'osm-feature-xxxgeeeituerijfhy7'}>
                <Popup>
                  <div>
                    <strong>Type:</strong> {feature.properties.type?.replace(/_/g, ' ')}<br/>
                    {feature.properties.name && <>
                      <strong>Name:</strong> {feature.properties.name}<br/>
                    </>}
                  </div>
                </Popup>
              </GeoJSON>);
            }
          })}

            {mapRef.current && addressInfo.dropLocationHistory?.filter((dl => !dl.suggestedForAddressFindId))
              .map(dropPoint => {
                return (<Pane name={`old-drop-point-${dropPoint?.id}`} key={`existing-drop-point-${dropPoint?.id}`}>
                  <Circle
                    center={[dropPoint?.latitude, dropPoint?.longitude]}
                    radius={dropPointRadiusInMetres / 2}
                    fillOpacity={0.5}
                    pathOptions={{color: '#899499'}}>
                  </Circle>
                </Pane>)
              })}
            {mapRef.current && addressInfo.suggestedDropLocations.map((suggestedDrop: DropLocation, index: number) => {
              return (<Pane name={`suggested-drops-${suggestedDrop.id}`} key={`suggested-drop-points-${suggestedDrop.id}`}>
                <Circle
                  center={[suggestedDrop.latitude, suggestedDrop.longitude]}
                  radius={dropPointRadiusInMetres / 2}
                  fillOpacity={0.8}
                  pathOptions={{color: '#0583FF'}}>
                </Circle>
              </Pane>)
            })}
          <Pane name="RoutePane" style={{ zIndex: 10000 }}>
            <Pane
              key="Geofences"
              name="Geofences"
              style={{ zIndex: indexPriority(9) }}>
              {geofences?.length > 0 &&
                geofences.filter(geofence => geofence.active === true)
                  .map(geofence => (<GeoJSON
                      data={geofence.geoJson}
                      style={{ fill: false, color: geofenceColor(geofence.id) }}
                      key={geofence.id}
                    />
                  ))}
            </Pane>
          </Pane>
          <Pane style={{ minWidth: '25px' }} name={`eircode-${eircode}`}>
            <Marker
              position={getEircodeCoords()}
              icon={new L.Icon({
                iconUrl: markerIconPng,
                iconSize: [25, 41],

              })}>
              <Tooltip
                permanent
                direction="bottom"
                opacity={0.6}
                offset={[0, 10]}>
                {'Eircode'}
              </Tooltip>
            </Marker>
          </Pane>
        </MapContainer> : <></>}

      </div>
      {addressInfo ? <div className="sm:col-span-3">
        <dl className="divide-y divide-gray-100">
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">Address</dt>
            <dd className={`text-sm leading-6 text-gray-700 sm:col-span-2 sm:-mt-0.5 ${checkIsWithinGeofence() ? '' : 'text-red-700'}`}>
              {addressInfo?.address || ''}
              {addressInfo && checkIsWithinGeofence() ? <div className="py-2 text-sm text-green-700">
                Inside geofence
              </div> : <div className="py-2 text-sm text-red-700 font-bold">
                Outside geofence
              </div>}
            </dd>
          </div>
          <div className="p-4">
            <label htmlFor="drop-point-remark" className="block text-sm font-medium leading-6 text-gray-900">
              If address is unsuitable, select a reason <span className='font-bold'>(visible to customer)</span>
            </label>
            <div className="mt-2">
              <SelectDropdown
                value={addressRemarkText}
                onChange={(v: string) => setAddressRemarkText(v)}
                options={addressRemarkDropdownOptions} />
            </div>
            <button
              type="button"
              onClick={() => {
                if (addressRemarkText) {
                  saveRemarkForAddress(addressInfo.id, addressRemarkText)
                    .then(() => setAddressRemarkText(''))
                }
              }}
              className={`my-3 inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
            >
              Save note
            </button>

          </div>
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">Past remarks</dt>
            <dd className="text-sm leading-6 text-gray-700 sm:col-span-2 sm:-mt-0.5">
              {addressRemarks?.length > 0 ? <ul className='flex-1 divide-y divide-gray-200 overflow-y-auto'>
                {addressRemarks?.map((remark: AddressFindRemark) => {
                  return (<li key={remark.id}>
                      <div className='group relative flex items-center px-1 py-2 cursor-pointer hover:bg-gray-200'>
                        {addressRemarkDropdownOptions.find(opt => opt.value === remark.message)?.displayText || remark.message}
                      </div>
                    </li>)
                })}
              </ul> : <span>None</span>}
            </dd>
          </div>
        </dl>
        <dt className="text-sm font-medium text-gray-900">{isAddressEnabled ? "Address Enabled" : "Address Disabled"} -  WARNING Changing switch value will immediately enable/disable address</dt>
        <Field as="div" className="flex items-center">
          <Switch
            disabled={isEnablingAddress}
            checked={isAddressEnabled}
            onChange={(isChecked) => enableAddress(isChecked)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${isAddressEnabled ? 'bg-blue-600' : 'bg-gray-200'}`}
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isAddressEnabled ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </Switch>
          <Label as="span" className="ml-3 text-sm">
            <span className="font-medium text-gray-900">{isAddressEnabled ? 'Disable' : 'Enable'} {eircode}</span>
          </Label>
        </Field>
        <button
          type="button"
          onClick={() => setShouldShowModal(true)}
          className={`my-3 inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
        >
          Suggest drop points
        </button>

      </div> : <></>}
    </div>
    {(shouldShowModal && addressInfo) ? (
      <SuggestedDropPointConfirmationModal
        eircode={addressInfo.eircode}
        obstacles={obstacles}
        handleClose={(didCreateSuggestedDrops) => {
          loadAddressInfo();
          setShouldShowModal(false);
          if (didCreateSuggestedDrops) {
            notify({
              variant: 'success',
              message: 'Drop points created.'
            });
          }
        }}
        geofences={geofences}
      />
    ) : null}
  </div>);
}
const mapStateToProps = (state: RootState) => ({
  cells: state.orders.cells,
  selectedCellId: state.orders.selectedCellId,
  geofences: state.geofences.geofences,
  obstacles: state.obstacles.list,
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  notify: (notification: Notification) => newNotification(notification)(dispatch),
  updateAddressEnabledStatus: (addressId: string, isenabled: boolean) => updateAddressEnabledStatus(addressId, isenabled)(dispatch),
  getGeofences: () => getGeofences()(dispatch),
  getCells: () => getCells()(dispatch),
  getObstacles: (cellId: number) => getObstacles(cellId)(dispatch),
});
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DropPointSuggestor);
